import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiShield, FiMail, FiPhone, FiMapPin, FiUser } = FiIcons;

const LegalNotice = ({ onClose }) => {
  // Scroll to top when opening
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
        <button
          onClick={onClose}
          className="group flex items-center text-gray-600 hover:text-blue-600 mb-8 transition-colors px-4 py-2 rounded-lg hover:bg-blue-50 -ml-4"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Zurück zum Assessment</span>
        </button>

        <div className="flex items-center mb-8 pb-6 border-b border-gray-100">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <SafeIcon icon={FiShield} className="text-2xl text-blue-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            Impressum & Haftung
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="space-y-6">
            <section className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-3 text-blue-600">
                <SafeIcon icon={FiMapPin} className="mr-2" />
                <h2 className="font-semibold text-lg">Unternehmen</h2>
              </div>
              <p className="text-gray-700 leading-relaxed pl-6 border-l-2 border-blue-200 ml-1">
                <strong>Internet Consulting Group Inc.</strong><br />
                304 S. Jones Blvd #3009<br />
                Las Vegas NV 89107<br />
                USA
              </p>
            </section>

            <section className="bg-gray-50 p-6 rounded-xl">
              <div className="flex items-center mb-3 text-blue-600">
                <SafeIcon icon={FiUser} className="mr-2" />
                <h2 className="font-semibold text-lg">Verantwortlich</h2>
              </div>
              <p className="text-sm text-gray-500 mb-2 pl-6">nach § 55 Abs. 2 RStV:</p>
              <p className="text-gray-700 leading-relaxed pl-6 border-l-2 border-blue-200 ml-1">
                <strong>Internet Consulting Group Inc.</strong><br />
                304 S. Jones Blvd #3009<br />
                Las Vegas NV 89107<br />
                USA
              </p>
            </section>
          </div>

          <div className="space-y-6">
            <section className="bg-gray-50 p-6 rounded-xl h-full">
              <div className="flex items-center mb-3 text-blue-600">
                <SafeIcon icon={FiMail} className="mr-2" />
                <h2 className="font-semibold text-lg">Kontakt</h2>
              </div>
              <div className="space-y-4 pl-6 border-l-2 border-blue-200 ml-1">
                <div className="flex items-start">
                  <span className="font-medium w-20 text-gray-900">Telefon:</span>
                  <a href="tel:1-239-645-4696" className="text-blue-600 hover:underline">1-239-645-4696</a>
                </div>
                <div className="flex items-start">
                  <span className="font-medium w-20 text-gray-900">E-Mail:</span>
                  <a href="mailto:internetconsultinggroup@gmail.com" className="text-blue-600 hover:underline break-all">internetconsultinggroup@gmail.com</a>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="space-y-8 text-gray-700">
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Haftungsausschluss</h2>
            
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Haftung für Inhalte</h3>
              <p className="text-sm leading-relaxed text-gray-600 text-justify">
                Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen.
              </p>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Haftung für Links</h3>
              <p className="text-sm leading-relaxed text-gray-600 text-justify">
                Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Urheberrecht</h3>
              <p className="text-sm leading-relaxed text-gray-600 text-justify">
                Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet. Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen.
              </p>
            </div>
          </section>
        </div>
      </div>
    </motion.div>
  );
};

export default LegalNotice;